import { DENSE_MATCH_STATUS } from "./stereo";

import { debugLog } from "../../../utils/debug";

const GPU_BUFFER_USAGE = window.GPUBufferUsage;
const GPU_MAP_MODE = window.GPUMapMode;

const WORKGROUP_SIZE_X = 8;
const WORKGROUP_SIZE_Y = 8;

const shaderCode = /* wgsl */ `
struct Config {
  width: i32,
  height: i32,
  minDisparity: i32,
  maxDisparity: i32,

  blockRadius: i32,
  pixelStep: i32,
  minimumScore: f32,
  ambiguityMargin: f32,

  leftRightThreshold: f32,
  _padding0: f32,
  _padding1: f32,
  _padding2: f32,
};

@group(0) @binding(0)
var<storage, read> imageA: array<f32>;

@group(0) @binding(1)
var<storage, read> imageB: array<f32>;

@group(0) @binding(2)
var<storage, read_write> disparities: array<f32>;

@group(0) @binding(3)
var<storage, read_write> confidence: array<f32>;

@group(0) @binding(4)
var<storage, read_write> matchStatus: array<u32>;

@group(0) @binding(5)
var<uniform> config: Config;

fn pixelIndex(x: i32, y: i32) -> u32 {
  return u32(y * config.width + x);
}

fn computeZNCC(
  referenceIsA: bool,
  xReference: i32,
  yReference: i32,
  xTarget: i32,
) -> f32 {
  var sumReference = 0.0;
  var sumTarget = 0.0;
  var sampleCount = 0.0;

  for (
    var offsetY = -config.blockRadius;
    offsetY <= config.blockRadius;
    offsetY = offsetY + 1
  ) {
    for (
      var offsetX = -config.blockRadius;
      offsetX <= config.blockRadius;
      offsetX = offsetX + 1
    ) {
      let referenceIndex = pixelIndex(
        xReference + offsetX,
        yReference + offsetY
      );

      let targetIndex = pixelIndex(
        xTarget + offsetX,
        yReference + offsetY
      );

      var referenceValue = 0.0;
      var targetValue = 0.0;

      if (referenceIsA) {
        referenceValue = imageA[referenceIndex];
        targetValue = imageB[targetIndex];
      } else {
        referenceValue = imageB[referenceIndex];
        targetValue = imageA[targetIndex];
      }

      sumReference = sumReference + referenceValue;
      sumTarget = sumTarget + targetValue;
      sampleCount = sampleCount + 1.0;
    }
  }

  let meanReference = sumReference / sampleCount;
  let meanTarget = sumTarget / sampleCount;

  var numerator = 0.0;
  var varianceReference = 0.0;
  var varianceTarget = 0.0;

  for (
    var offsetY = -config.blockRadius;
    offsetY <= config.blockRadius;
    offsetY = offsetY + 1
  ) {
    for (
      var offsetX = -config.blockRadius;
      offsetX <= config.blockRadius;
      offsetX = offsetX + 1
    ) {
      let referenceIndex = pixelIndex(
        xReference + offsetX,
        yReference + offsetY
      );

      let targetIndex = pixelIndex(
        xTarget + offsetX,
        yReference + offsetY
      );

      var referenceValue = 0.0;
      var targetValue = 0.0;

      if (referenceIsA) {
        referenceValue = imageA[referenceIndex];
        targetValue = imageB[targetIndex];
      } else {
        referenceValue = imageB[referenceIndex];
        targetValue = imageA[targetIndex];
      }

      let centeredReference =
        referenceValue - meanReference;

      let centeredTarget =
        targetValue - meanTarget;

      numerator =
        numerator + centeredReference * centeredTarget;

      varianceReference =
        varianceReference +
        centeredReference * centeredReference;

      varianceTarget =
        varianceTarget +
        centeredTarget * centeredTarget;
    }
  }

  let denominator =
    sqrt(varianceReference * varianceTarget);

  if (denominator <= 0.000000000001) {
    return -1.0;
  }

  return numerator / denominator;
}

fn scoreForDisparity(
  referenceIsA: bool,
  x: i32,
  y: i32,
  disparity: i32,
  direction: i32,
) -> f32 {
  let targetX = x + direction * disparity;

  if (
    targetX - config.blockRadius < 0 ||
    targetX + config.blockRadius >= config.width
  ) {
    return -2.0;
  }

  return computeZNCC(
    referenceIsA,
    x,
    y,
    targetX
  );
}

fn findBestIntegerDisparity(
  referenceIsA: bool,
  x: i32,
  y: i32,
  direction: i32,
) -> vec4<f32> {
  var bestDisparity = 0;
  var bestScore = -1000.0;

  for (
    var disparity = config.minDisparity;
    disparity <= config.maxDisparity;
    disparity = disparity + 1
  ) {
    let score = scoreForDisparity(
      referenceIsA,
      x,
      y,
      disparity,
      direction
    );

    if (score > bestScore) {
      bestScore = score;
      bestDisparity = disparity;
    }
  }

  if (bestScore < -1.5) {
    return vec4<f32>(
      0.0,
      -1000.0,
      -1000.0,
      0.0
    );
  }

  var secondBestScore = -1000.0;

  for (
    var disparity = config.minDisparity;
    disparity <= config.maxDisparity;
    disparity = disparity + 1
  ) {
    if (abs(disparity - bestDisparity) <= 2) {
      continue;
    }

    let score = scoreForDisparity(
      referenceIsA,
      x,
      y,
      disparity,
      direction
    );

    if (score > secondBestScore) {
      secondBestScore = score;
    }
  }

  return vec4<f32>(
    f32(bestDisparity),
    bestScore,
    secondBestScore,
    1.0
  );
}

fn refineSubpixel(
  referenceIsA: bool,
  x: i32,
  y: i32,
  disparity: i32,
  direction: i32,
  centerScore: f32,
) -> f32 {
  if (
    disparity <= config.minDisparity ||
    disparity >= config.maxDisparity
  ) {
    return f32(disparity);
  }

  let scoreBefore = scoreForDisparity(
    referenceIsA,
    x,
    y,
    disparity - 1,
    direction
  );

  let scoreAfter = scoreForDisparity(
    referenceIsA,
    x,
    y,
    disparity + 1,
    direction
  );

  if (
    scoreBefore < -1.5 ||
    scoreAfter < -1.5
  ) {
    return f32(disparity);
  }

  let denominator =
    scoreBefore -
    2.0 * centerScore +
    scoreAfter;

  if (abs(denominator) < 0.000000000001) {
    return f32(disparity);
  }

  var offset =
    0.5 *
    (scoreBefore - scoreAfter) /
    denominator;

  offset = clamp(offset, -1.0, 1.0);

  return f32(disparity) + offset;
}

@compute
@workgroup_size(8, 8)
fn main(
  @builtin(global_invocation_id)
  globalId: vec3<u32>
) {
  let x = i32(globalId.x);
  let y = i32(globalId.y);

  if (
    x >= config.width ||
    y >= config.height
  ) {
    return;
  }

  let index = pixelIndex(x, y);

  disparities[index] = 0.0;
  confidence[index] = 0.0;
  matchStatus[index] = 0u;

  if (
    x % config.pixelStep != 0 ||
    y % config.pixelStep != 0
  ) {
    return;
  }

  if (
    y < config.blockRadius ||
    y >= config.height - config.blockRadius
  ) {
    return;
  }

  let minimumX =
    config.blockRadius +
    max(0, config.minDisparity);

  let maximumX =
    config.width -
    config.blockRadius -
    max(0, -config.maxDisparity);

  if (
    x < minimumX ||
    x >= maximumX
  ) {
    return;
  }

  let forward = findBestIntegerDisparity(
    true,
    x,
    y,
    -1
  );

  if (forward.w < 0.5) {
    return;
  }

  let forwardInteger = i32(forward.x);
  let forwardScore = forward.y;
  let secondBestScore = forward.z;

  if (forwardScore < config.minimumScore) {
    matchStatus[index] = 2u;
    return;
  }

  var ambiguity = 0.0;

  if (secondBestScore > -999.0) {
    ambiguity = forwardScore - secondBestScore;

    if (ambiguity < config.ambiguityMargin) {
      matchStatus[index] = 3u;
      return;
    }
  }

  let forwardDisparity = refineSubpixel(
    true,
    x,
    y,
    forwardInteger,
    -1,
    forwardScore
  );

  let matchedX = x - forwardInteger;

  if (
    matchedX - config.blockRadius < 0 ||
    matchedX + config.blockRadius >= config.width
  ) {
    matchStatus[index] = 4u;
    return;
  }

  let reverse = findBestIntegerDisparity(
    false,
    matchedX,
    y,
    1
  );

  if (reverse.w < 0.5) {
    matchStatus[index] = 4u;
    return;
  }

  let reverseInteger = i32(reverse.x);
  let reverseScore = reverse.y;
  let reverseSecondBest = reverse.z;

  if (reverseScore < config.minimumScore) {
    matchStatus[index] = 4u;
    return;
  }

  if (
    reverseSecondBest > -999.0 &&
    reverseScore - reverseSecondBest <
      config.ambiguityMargin
  ) {
    matchStatus[index] = 4u;
    return;
  }

  let reverseDisparity = refineSubpixel(
    false,
    matchedX,
    y,
    reverseInteger,
    1,
    reverseScore
  );

  if (
    abs(forwardDisparity - reverseDisparity) >
    config.leftRightThreshold
  ) {
    matchStatus[index] = 5u;
    return;
  }

  disparities[index] = forwardDisparity;

  let normalizedAmbiguity =
    clamp(ambiguity / 0.25, 0.0, 1.0);

  confidence[index] =
    forwardScore * normalizedAmbiguity;

  matchStatus[index] = 1u;
}
`;

function imageToGrayscaleFloat32(imageElement) {
  const width = imageElement.naturalWidth || imageElement.width;
  const height = imageElement.naturalHeight || imageElement.height;

  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", {
    willReadFrequently: true,
  });

  if (!context) {
    throw new Error("Unable to create canvas context for WebGPU stereo.");
  }

  context.drawImage(imageElement, 0, 0, width, height);

  const rgba = context.getImageData(0, 0, width, height).data;
  const grayscale = new Float32Array(width * height);

  for (let index = 0; index < width * height; index += 1) {
    const rgbaIndex = index * 4;

    const red = rgba[rgbaIndex];
    const green = rgba[rgbaIndex + 1];
    const blue = rgba[rgbaIndex + 2];

    grayscale[index] = 0.299 * red + 0.587 * green + 0.114 * blue;
  }

  return {
    width,
    height,
    data: grayscale,
  };
}

function createStorageBuffer(device, data, usage) {
  const buffer = device.createBuffer({
    size: data.byteLength,
    usage,
    mappedAtCreation: true,
  });

  const constructor = data.constructor;

  new constructor(buffer.getMappedRange()).set(data);

  buffer.unmap();

  return buffer;
}

function createConfigBuffer(
  device,
  {
    width,
    height,
    minDisparity,
    maxDisparity,
    blockRadius,
    pixelStep,
    minimumScore,
    ambiguityMargin,
    leftRightThreshold,
  },
) {
  const data = new ArrayBuffer(48);
  const view = new DataView(data);

  view.setInt32(0, width, true);
  view.setInt32(4, height, true);
  view.setInt32(8, minDisparity, true);
  view.setInt32(12, maxDisparity, true);

  view.setInt32(16, blockRadius, true);
  view.setInt32(20, pixelStep, true);

  view.setFloat32(24, minimumScore, true);
  view.setFloat32(28, ambiguityMargin, true);

  view.setFloat32(32, leftRightThreshold, true);

  const buffer = device.createBuffer({
    size: data.byteLength,
    usage: GPU_BUFFER_USAGE.UNIFORM | GPU_BUFFER_USAGE.COPY_DST,
  });

  device.queue.writeBuffer(buffer, 0, data);

  return buffer;
}

export function isWebGPUStereoAvailable() {
  return Boolean(typeof navigator !== "undefined" && navigator.gpu);
}

export async function computeDenseDisparityWebGPU(
  imageElementA,
  imageElementB,
  options = {},
) {
  if (!isWebGPUStereoAvailable()) {
    throw new Error("WebGPU is not available in this browser.");
  }

  const {
    minDisparity = 1,
    maxDisparity = 256,
    blockRadius = 3,
    minimumScore = 0.75,
    ambiguityMargin = 0.015,
    leftRightThreshold = 1,
    pixelStep = 1,
  } = options;

  const [grayscaleA, grayscaleB] = [
    imageToGrayscaleFloat32(imageElementA),
    imageToGrayscaleFloat32(imageElementB),
  ];

  if (
    grayscaleA.width !== grayscaleB.width ||
    grayscaleA.height !== grayscaleB.height
  ) {
    throw new Error("Stereo images must have identical dimensions.");
  }

  const width = grayscaleA.width;
  const height = grayscaleA.height;
  const pixelCount = width * height;

  const adapter = await navigator.gpu.requestAdapter();

  if (!adapter) {
    throw new Error("Unable to acquire a WebGPU adapter.");
  }

  const device = await adapter.requestDevice();

  const shaderModule = device.createShaderModule({
    code: shaderCode,
  });

  const pipeline = device.createComputePipeline({
    layout: "auto",
    compute: {
      module: shaderModule,
      entryPoint: "main",
    },
  });

  const inputUsage = GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_DST;
  const outputUsage = GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_SRC;

  const imageABuffer = createStorageBuffer(device, grayscaleA.data, inputUsage);
  const imageBBuffer = createStorageBuffer(device, grayscaleB.data, inputUsage);

  const disparityBuffer = device.createBuffer({
    size: pixelCount * Float32Array.BYTES_PER_ELEMENT,
    usage: outputUsage,
  });

  const confidenceBuffer = device.createBuffer({
    size: pixelCount * Float32Array.BYTES_PER_ELEMENT,
    usage: outputUsage,
  });

  // WGSL uses u32 status values; readback converts them to Uint8Array.
  const statusBuffer = device.createBuffer({
    size: pixelCount * Uint32Array.BYTES_PER_ELEMENT,
    usage: outputUsage,
  });

  const configBuffer = createConfigBuffer(device, {
    width,
    height,
    minDisparity,
    maxDisparity,
    blockRadius,
    pixelStep,
    minimumScore,
    ambiguityMargin,
    leftRightThreshold,
  });

  const bindGroup = device.createBindGroup({
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      {
        binding: 0,
        resource: {
          buffer: imageABuffer,
        },
      },
      {
        binding: 1,
        resource: {
          buffer: imageBBuffer,
        },
      },
      {
        binding: 2,
        resource: {
          buffer: disparityBuffer,
        },
      },
      {
        binding: 3,
        resource: {
          buffer: confidenceBuffer,
        },
      },
      {
        binding: 4,
        resource: {
          buffer: statusBuffer,
        },
      },
      {
        binding: 5,
        resource: {
          buffer: configBuffer,
        },
      },
    ],
  });

  const disparityReadBuffer = device.createBuffer({
    size: pixelCount * Float32Array.BYTES_PER_ELEMENT,
    usage: GPU_BUFFER_USAGE.COPY_DST | GPU_BUFFER_USAGE.MAP_READ,
  });

  const confidenceReadBuffer = device.createBuffer({
    size: pixelCount * Float32Array.BYTES_PER_ELEMENT,
    usage: GPU_BUFFER_USAGE.COPY_DST | GPU_BUFFER_USAGE.MAP_READ,
  });

  const statusReadBuffer = device.createBuffer({
    size: pixelCount * Uint32Array.BYTES_PER_ELEMENT,
    usage: GPU_BUFFER_USAGE.COPY_DST | GPU_BUFFER_USAGE.MAP_READ,
  });

  const encoder = device.createCommandEncoder();
  const pass = encoder.beginComputePass();

  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);

  pass.dispatchWorkgroups(
    Math.ceil(width / WORKGROUP_SIZE_X),
    Math.ceil(height / WORKGROUP_SIZE_Y),
  );

  pass.end();

  encoder.copyBufferToBuffer(
    disparityBuffer,
    0,
    disparityReadBuffer,
    0,
    pixelCount * Float32Array.BYTES_PER_ELEMENT,
  );

  encoder.copyBufferToBuffer(
    confidenceBuffer,
    0,
    confidenceReadBuffer,
    0,
    pixelCount * Float32Array.BYTES_PER_ELEMENT,
  );

  encoder.copyBufferToBuffer(
    statusBuffer,
    0,
    statusReadBuffer,
    0,
    pixelCount * Uint32Array.BYTES_PER_ELEMENT,
  );

  device.queue.submit([encoder.finish()]);

  await Promise.all([
    disparityReadBuffer.mapAsync(GPU_MAP_MODE.READ),
    confidenceReadBuffer.mapAsync(GPU_MAP_MODE.READ),
    statusReadBuffer.mapAsync(GPU_MAP_MODE.READ),
  ]);

  const disparities = new Float32Array(
    disparityReadBuffer.getMappedRange().slice(0),
  );

  const confidence = new Float32Array(
    confidenceReadBuffer.getMappedRange().slice(0),
  );

  const gpuStatus = new Uint32Array(statusReadBuffer.getMappedRange().slice(0));

  const matchStatus = new Uint8Array(pixelCount);

  for (let index = 0; index < pixelCount; index += 1) {
    matchStatus[index] = gpuStatus[index];

    if (matchStatus[index] !== DENSE_MATCH_STATUS.ACCEPTED) {
      disparities[index] = Number.NaN;
      confidence[index] = Number.NaN;
    }
  }

  disparityReadBuffer.unmap();
  confidenceReadBuffer.unmap();
  statusReadBuffer.unmap();

  imageABuffer.destroy();
  imageBBuffer.destroy();
  disparityBuffer.destroy();
  confidenceBuffer.destroy();
  statusBuffer.destroy();
  configBuffer.destroy();

  disparityReadBuffer.destroy();
  confidenceReadBuffer.destroy();
  statusReadBuffer.destroy();

  let acceptedCount = 0;

  for (let index = 0; index < matchStatus.length; index += 1) {
    if (matchStatus[index] === DENSE_MATCH_STATUS.ACCEPTED) {
      acceptedCount += 1;
    }
  }

  debugLog("[Reconstruction] WebGPU dense stereo:", {
    width,
    height,
    minDisparity,
    maxDisparity,
    pixelStep,
    acceptedCount,
  });

  return {
    width,
    height,
    disparities,
    confidence,
    matchStatus,
    minDisparity,
    maxDisparity,
    pixelStep,
    backend: "webgpu",
  };
}
